import {
  CLEAR_ALBUM,
  CLEAR_POST,
  DELETE_ALBUM,
  DELETE_POST,
  GET_ALBUM,
  GET_ALBUMS,
  GET_AUTHORS,
  GET_POST,
  GET_POSTS,
  DELETE_SONG,
  POST_ALBUM,
  POST_POST,
  PUT_SONG,
  POST_SONG,
  SET_AUTHOR,
  SET_ALBUM_FIELD,
  SET_POST_FIELD,
  SET_SINGLE_FIELD,
} from './actions';
import { GET_GENRES } from '../charts/actions';

const initialState = {
  /**
   * Used to search for authors to change albums, singles or post ownership
   */
  authors: [],

  /**
   * All genres in the platform indexed by ID
   */
  genresIndex: {},

  /**
   * All albums for the current author
   */
  albums: {
    total: 0,
    list: [],
  },

  /**
   * The album to be edited or added
   */
  album: {
    author: {},
    songs: [],
    genres: [],
    title: '',
    description: '',
    published: false,
    release_date: '',
    copyright: '',
    phonographic: '',
  },

  /**
   * List of all singles in the system
   */
  singles: {
    total: 0,
    list: [],
  },

  /**
   * The single an author/admin is editing
   */
  single: {
    author: {},
    genres: [],
    song: {},
    title: '',
    description: '',
    published: false,
    release_date: '',
    copyright: '',
    phonographic: '',
  },

  /**
   * Manage the blog for authors and admins
   */
  posts: {
    total: 0,
    list: [],
  },

  /**
   * The current editing post
   */
  post: {
    author: {},
    title: '',
    published: false,
    featured: false,
    allow_comments: true,
    edited: [{ type: 'paragraph', children: [{ text: '' }] }],
  },
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GET_AUTHORS.REQUEST:
      return {
        ...state,
        album: {
          ...state.album,
          author: {},
        },
        single: {
          ...state.single,
          author: {},
        },
        post: {
          ...state.single,
          author: {},
        },
      };
    case GET_AUTHORS.SUCCESS:
      return {
        ...state,
        authors: action.payload.artists,
      };
    case DELETE_ALBUM.SUCCESS:
      return {
        ...state,
        albums: {
          ...state.albums,
          list: state.albums.list.filter(album => album.id !== action.data.album.id),
        }
      };
    case CLEAR_ALBUM:
      return {
        ...state,
        album: initialState.album,
        single: initialState.single,
      };
    case GET_ALBUM.SUCCESS: {
      const album = action.payload.album;
      const genres = album.genres.map((id) => ({ id }));
      const year = (new Date(album.released)).getFullYear();
      return {
        ...state,
        album: {
          ...album,
          release_date: year,
          genres,
          songs: album.songs.map(sng => ({
            ...sng,
            ...getValuesFromStrings(sng),
            tags: sng.tags.map(tag => tag.name).join(', '),
          })),
        },
        single: {
          ...album,
          release_date: year,
          genres,
          song: album.songs.length > 0
            ? {
              ...album.songs[0],
              ...getValuesFromStrings(album.songs[0]),
              tags: album.songs[0].tags.map(tag => tag.name).join(', '),
            }
            : {},
        },
      };
    }
    case POST_ALBUM.SUCCESS:
      return {
        ...state,
        album: {
          ...state.album,
          id: action.payload.album.id,
        },
        single: {
          ...state.single,
          id: action.payload.album.id,
        },
      };
    case POST_SONG.SUCCESS:
      return {
        ...state,
        album: {
          ...state.album,
          songs: [...state.album.songs, {
            ...action.payload.song,
            ...getValuesFromStrings(action.payload.song),
            tags: action.payload.song.tags.map(tag => tag.name).join(', '),
          }]
        },
        single: {
          ...state.single,
          song: {
            ...state.single.song,
            ...action.payload.song,
            ...getValuesFromStrings(action.payload.song),
            tags: action.payload.song.tags.map(tag => tag.name).join(', '),
          },
        },
      };
    case PUT_SONG.SUCCESS:
      return {
        ...state,
        album: {
          ...state.album,
          songs: state.album.songs.map((sng) => {
            if (sng.id === action.payload.song.id) {
              return {
                ...action.payload.song,
                ...getValuesFromStrings(action.payload.song),
                tags: action.payload.song.tags.map(tag => tag.name).join(', '),
              };
            }
            return sng;
          }),
        },
      };
    case DELETE_SONG.SUCCESS:
      return {
        ...state,
        album: {
          ...state.album,
          songs: state.album.songs.filter(sng => sng.id !== action.data.songId),
        },
      };
    case GET_ALBUMS.SUCCESS:
      return {
        ...state,
        albums: {
          ...state.albums,
          total: action.payload.meta.total,
          list: action.payload.albums,
        },
      };
    case GET_GENRES.SUCCESS:
      return {
        ...state,
        genresIndex: action.payload.genres.reduce((index, genre) => {
          index[genre.id] = genre;
          return index;
        }, {}),
      };
    case SET_ALBUM_FIELD:
      return {
        ...state,
        album: {
          ...state.album,
          [action.payload.name]: action.payload.value,
        },
      };
    case GET_POSTS.SUCCESS:
      return {
        ...state,
        posts: {
          total: action.payload.meta.total,
          list: action.payload.posts,
        },
      };
    case GET_POST.SUCCESS:
      return {
        ...state,
        post: {
          ...action.payload.post,
          edited: state.post.edited,
        },
      };
    case POST_POST.SUCCESS:
      return {
        ...state,
        post: {
          id: action.payload.post.id,
          slug: action.payload.post.slug,
          ...state.post,
        },
      };
    case DELETE_POST.SUCCESS:
      return {
        ...state,
        posts: {
          total: state.posts.total - 1,
          list: state.posts.list.filter(post => post.id !== action.data.postId),
        }
      };
    case CLEAR_POST:
      return {
        ...state,
        post: {
          ...initialState.post,
          author: action.payload.author,
        },
      };
    case SET_POST_FIELD:
      return {
        ...state,
        post: {
          ...state.post,
          [action.payload.name]: action.payload.value,
        },
      };
    case SET_AUTHOR:
      return {
        ...state,
        album: {
          ...state.album,
          author: action.payload.author,
        },
        single: {
          ...state.single,
          author: action.payload.author,
        },
        post: {
          ...state.post,
          author: action.payload.author,
        },
      };
    case SET_SINGLE_FIELD:
      if (action.payload.name === 'song') {
        return {
          ...state,
          single: {
            ...state.single,
            song: {
              ...state.single.song,
              ...action.payload.value,
            },
          },
        };
      }

      return {
        ...state,
        single: {
          ...state.single,
          [action.payload.name]: action.payload.value,
        },
      };
    default:
      return state;
  }
}

function getValuesFromStrings(sng) {
  return {
    bpm: sng.bpm ? { value: sng.bpm, label: sng.bpm } : null,
    moods: sng.moods ? sng.moods.split(',').map(value => ({ value, label: value })) : [],
    instruments: sng.instruments ? sng.instruments.split(',').map(value => ({ value, label: value })) : [],
    vocals: sng.vocals ? sng.vocals.split(',').map(value => ({ value, label: value })) : [],
  };
}